/* eslint-disable react/no-array-index-key */
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Button, IconButton, TextField } from "@mui/material";

import { RootState } from "../../../store";
import { paymentOptions, paymentOptionsEnum } from "./BillSummary";
import { tl } from "../../../components/translate";
import * as billActions from "../../../actions/bill";
import PaymentMethodSelect from "./PaymentMethodSelect";
import NrsFormat from "../../../components/FormattingInput/NrsInput";
import { BillDocumentType, PaymentBreakdown } from "../../../interfaces/BillInterfaces";

interface MultiplePaymentMethodSelectProps {
  isCreditNote?: boolean;
}

export default function MultiplePaymentMethodSelect({
  isCreditNote = false
}: MultiplePaymentMethodSelectProps): React.ReactElement {
  const dispatch = useDispatch();
  const draft: BillDocumentType = useSelector((state: RootState) => state.bills.draft);

  // helpers
  const clientBalance = draft?.client?.balance ?? 0;
  const totalAmount = draft?.summary?.totalAmount ?? 0;

  //  memos
  const paymentBreakdown = React.useMemo(
    () => draft.paymentInfo?.paymentBreakdown || [],
    [draft.paymentInfo?.paymentBreakdown]
  );

  const paidAmount = React.useMemo(
    () => paymentBreakdown.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [paymentBreakdown]
  );

  // update draft
  const updatePaymentBreakdown = React.useCallback(
    (updated: Array<PaymentBreakdown>) => {
      const sum = updated.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);

      const newDraft = {
        ...draft,
        paymentInfo: {
          ...draft.paymentInfo,
          paidAmount: sum,
          paid: sum >= totalAmount,
          paymentBreakdown: updated
        }
      };

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - ignore ts error for now
      dispatch(billActions.updateDraftAction(newDraft));
    },
    [dispatch, draft, totalAmount]
  );

  // handle method change
  const handleMethodChange = React.useCallback(
    (index: number, method: paymentOptionsEnum) => {
      if (draft.paymentInfo.paymentBreakdown.find((p) => p.method === method)) return;

      if (isCreditNote) {
        const updated = [{ method, amount: draft?.refundInfo?.maximumRefundable || 0 }];
        updatePaymentBreakdown(updated);
      } else {
        const updated = paymentBreakdown.map((p, i) => (i === index ? { ...p, method } : p));
        updatePaymentBreakdown(updated);
      }
    },
    [
      draft.paymentInfo.paymentBreakdown,
      draft.refundInfo?.maximumRefundable,
      isCreditNote,
      paymentBreakdown,
      updatePaymentBreakdown
    ]
  );

  // handle amount change
  const handleAmountChange = React.useCallback(
    (index: number, value: number) => {
      const updated = paymentBreakdown.map((p, i) =>
        i === index ? { ...p, amount: value || 0 } : p
      );

      let newPaidAmount = updated.reduce((sum, p) => sum + p.amount, 0);
      if (newPaidAmount > totalAmount) {
        const excess = newPaidAmount - totalAmount;
        updated[index].amount = Math.max(0, (value || 0) - excess);
        newPaidAmount = totalAmount;
      }

      updatePaymentBreakdown(updated);
    },
    [paymentBreakdown, totalAmount, updatePaymentBreakdown]
  );

  // add payment breakdown row
  const addPaymentBreakdownRow = React.useCallback(() => {
    if (paidAmount >= totalAmount) return;

    // Find available payment methods not already used
    const usedMethods = new Set(paymentBreakdown.map((p) => p.method));
    const availableMethods = Object.values(paymentOptionsEnum).filter(
      (method) =>
        !usedMethods.has(method) &&
        method !== paymentOptionsEnum.balance &&
        method !== paymentOptionsEnum.noPayment
    );

    const newMethod = availableMethods.length > 0 ? availableMethods[0] : null;
    if (!newMethod) return;

    const remaining = Math.max(0, totalAmount - paidAmount);
    const updated = [...paymentBreakdown, { method: newMethod, amount: remaining }];

    updatePaymentBreakdown(updated);
  }, [paymentBreakdown, paidAmount, totalAmount, updatePaymentBreakdown]);

  // remove payment breakdown row
  const removePaymentBreakdownRow = React.useCallback(
    (index: number) => {
      if (paymentBreakdown.length === 1) return;
      const updated = paymentBreakdown.filter((_, i) => i !== index);

      updatePaymentBreakdown(updated);
    },
    [paymentBreakdown, updatePaymentBreakdown]
  );

  return (
    <Box>
      <Box>
        {paymentBreakdown.map((row, index: number) => {
          // Calculate available options for this specific row
          const rowPaymentOpts = paymentOptions.filter((option) => {
            // Always exclude noPayment
            if (option === paymentOptionsEnum.noPayment) return false;

            // Exclude balance if not applicable
            if (option === paymentOptionsEnum.balance && (draft?.client?.balance || 0) <= 0) {
              return false;
            }

            // check if this option is selected in ANY OTHER row
            const isSelectedInOtherRow = paymentBreakdown.some(
              (p, i) => i !== index && p.method === option
            );
            if (isSelectedInOtherRow) return false;

            return true;
          });

          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1
              }}
            >
              <PaymentMethodSelect
                selectedPaymentMethod={row.method}
                paymentOptionsProp={rowPaymentOpts}
                onPaymentMethodChange={(_, v) => {
                  if (!v) return;
                  handleMethodChange(index, v as paymentOptionsEnum);
                }}
                isCreditNote={false}
                sxProp={{ minWidth: "200px" }}
                TxtFieldProps={{ size: "small", sx: { textAlign: "left" } }}
                disabled={
                  row.method === paymentOptionsEnum.balance ||
                  (isCreditNote && draft?.refundInfo?.maximumRefundable === 0)
                }
              />

              <TextField
                data-testmation="amountInput"
                size="small"
                variant="outlined"
                value={Number(row.amount || 0)}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const value = e.target.value === "" ? 0 : Number(e.target.value);
                  handleAmountChange(index, value);
                }}
                slotProps={{
                  input: {
                    inputComponent: NrsFormat,
                    startAdornment: "Rs",
                    sx: { "& input": { textAlign: "right" } }
                  },
                  inputLabel: { shrink: true }
                }}
                disabled={row.method === paymentOptionsEnum.balance || isCreditNote}
              />

              <IconButton
                size="small"
                onClick={() => removePaymentBreakdownRow(index)}
                disabled={
                  paymentBreakdown.length === 1 ||
                  row.method === paymentOptionsEnum.balance ||
                  isCreditNote
                }
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      <Button
        size="small"
        variant="outlined"
        onClick={addPaymentBreakdownRow}
        disabled={
          (clientBalance > 0 && clientBalance >= totalAmount) ||
          paidAmount >= totalAmount ||
          isCreditNote
        }
        sx={{ mb: 1 }}
      >
        {tl("billing.addPaymentMethod")}
      </Button>
    </Box>
  );
}
